import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { btnBlue, btnGhost, btnRed, card, dayLabel, iso, Tag } from "@/components/club/ui";
import { Roster } from "@/components/club/Roster";

const SPAN = { dia: 1, semana: 7, mes: 31 };

const SessionCard = ({ s, onChange, onRoster }) => {
  const act = async (path) => {
    try {
      const { data } = await api.post(`/club/sessions/${s.id}/${path}`);
      const msg = path === "book"
        ? (data.status === "waitlist" ? "Clase llena: estás en lista de espera" : data.paid_with === "pass" ? `Reserva hecha · te quedan ${data.remaining} clases en tu bono` : data.paid_with === "single" ? "Reserva hecha · clase suelta, págala en el club" : "Reserva hecha")
        : (data.refunded ? "Reserva cancelada · clase devuelta a tu bono" : data.had_pass ? "Reserva cancelada · con menos de 24 h la clase no se devuelve al bono" : "Reserva cancelada");
      toast.success(msg);
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    const reason = window.prompt("Motivo de la cancelación (lluvia, lesión…)");
    if (!reason) return;
    try { await api.post(`/club/sessions/${s.id}/cancel`, { reason }); toast.success("Clase cancelada y alumnos avisados"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const full = s.booked_count >= s.capacity;
  return (
    <div className={`${card} flex flex-wrap items-center gap-4 ${s.status === "cancelled" ? "opacity-60" : ""}`} data-testid={`session-${s.id}`}>
      <div className="w-16 text-center"><p className="font-display font-black text-2xl text-[#0B1D3A]">{s.time}</p><p className="text-xs text-slate-500">{s.duration_min} min</p></div>
      <div className="flex-1 min-w-[180px]">
        <p className="font-bold text-lg text-[#0B1D3A]">{s.title}</p>
        <p className="text-sm text-slate-600">{s.discipline} · {s.level} · {s.instructor_name}{s.arena ? ` · ${s.arena}` : ""}{s.horse ? ` · ${s.horse}` : ""}</p>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {s.visibility === "private" && <Tag tone="gray">Privada</Tag>}
          {s.status === "cancelled" ? <Tag tone="red">Cancelada · {s.cancel_reason}</Tag> : <Tag tone={full ? "red" : "green"}>{s.booked_count}/{s.capacity} plazas{s.waitlist_count ? ` · ${s.waitlist_count} en espera` : ""}</Tag>}
          {s.mine && <Tag>{s.mine === "booked" ? "Reservada" : "En lista de espera"}</Tag>}
          {s.mine === "booked" && s.my_pay && <Tag tone={s.my_pay === "pass" ? "green" : "gray"}>{s.my_pay === "pass" ? "Con bono" : "Suelta · pagar en el club"}</Tag>}
        </div>
      </div>
      {s.status === "active" && (
        <div className="flex flex-wrap gap-2">
          {s.mine ? <button className={btnGhost} onClick={() => act("unbook")} data-testid={`unbook-${s.id}`}>Cancelar reserva</button>
            : <button className={btnRed} onClick={() => act("book")} data-testid={`book-${s.id}`}>{full ? "Lista de espera" : "Reservar"}</button>}
          {s.staff && <button className={btnBlue} onClick={() => onRoster(s)} data-testid={`roster-${s.id}`}><Users size={16} />Alumnos</button>}
          {s.staff && <button className={btnGhost} onClick={cancel} data-testid={`cancel-session-${s.id}`}><X size={16} />Cancelar clase</button>}
        </div>
      )}
    </div>
  );
};

export const Agenda = ({ clubId }) => {
  const [view, setView] = useState("semana");
  const [from, setFrom] = useState(() => new Date());
  const [rows, setRows] = useState(null);
  const [roster, setRoster] = useState(null);
  const to = new Date(from); to.setDate(to.getDate() + SPAN[view] - 1);

  const load = useCallback(() => {
    api.get(`/club/clubs/${clubId}/sessions`, { params: { start: iso(from), end: iso(to) } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [clubId, from, view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const move = (dir) => { const d = new Date(from); d.setDate(d.getDate() + dir * SPAN[view]); setFrom(d); };
  const days = [...new Set((rows || []).map((r) => r.date))];

  return (
    <div data-testid="club-agenda">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="inline-flex rounded-full bg-slate-100 p-1">
          {Object.keys(SPAN).map((v) => <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-full font-bold text-sm capitalize transition-colors ${view === v ? "bg-[#0B1D3A] text-white" : "text-slate-600"}`} data-testid={`agenda-view-${v}`}>{v === "dia" ? "Día" : v}</button>)}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => move(-1)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Anterior" data-testid="agenda-prev"><ChevronLeft /></button>
          <p className="font-bold text-[#0B1D3A] min-w-[150px] text-center">{dayLabel(iso(from))}{view !== "dia" && ` – ${dayLabel(iso(to))}`}</p>
          <button onClick={() => move(1)} className="p-2 rounded-full hover:bg-slate-100" aria-label="Siguiente" data-testid="agenda-next"><ChevronRight /></button>
        </div>
      </div>
      {rows === null ? <p className="text-slate-500">Cargando…</p> : view === "mes" ? (
        <div className="grid grid-cols-7 gap-1.5" data-testid="agenda-month">
          {["L", "M", "X", "J", "V", "S", "D"].map((d) => <p key={d} className="text-center text-xs font-bold text-slate-500">{d}</p>)}
          {Array.from({ length: (from.getDay() + 6) % 7 }).map((_, i) => <span key={`b${i}`} />)}
          {Array.from({ length: SPAN.mes }).map((_, i) => {
            const d = new Date(from); d.setDate(d.getDate() + i);
            const n = rows.filter((r) => r.date === iso(d) && r.status === "active").length;
            return (
              <button key={i} onClick={() => { setFrom(d); setView("dia"); }} className={`aspect-square rounded-xl border text-sm font-bold flex flex-col items-center justify-center transition-colors ${n ? "border-[#3D88B8] bg-[#EAF3F9] text-[#0B1D3A] hover:bg-[#3D88B8] hover:text-white" : "border-slate-200 text-slate-400"}`} data-testid={`month-day-${iso(d)}`}>
                {d.getDate()}{n > 0 && <span className="text-[10px] font-normal">{n} clase{n > 1 ? "s" : ""}</span>}
              </button>
            );
          })}
        </div>
      ) : !rows.length ? <p className={`${card} text-slate-600`} data-testid="agenda-empty">No hay clases en estas fechas.</p> : (
        <div className="space-y-6">
          {days.map((d) => (
            <div key={d}>
              <h3 className="font-display font-black uppercase text-[#3D88B8] mb-2">{dayLabel(d)}</h3>
              <div className="space-y-3">{rows.filter((r) => r.date === d).map((s) => <SessionCard key={s.id} s={s} onChange={load} onRoster={setRoster} />)}</div>
            </div>
          ))}
        </div>
      )}
      {roster && <Roster session={roster} onClose={() => setRoster(null)} />}
    </div>
  );
};
